import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { promotionsAPI, aiAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, Edit2, Trash2, Eye, MousePointer, 
  Zap, ExternalLink, Pause, Play, Sparkles, Loader2, Image, X
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Technology',
  'Business',
  'Marketing',
  'Finance',
  'Health',
  'Education',
  'Entertainment',
  'Lifestyle',
  'Other'
];

const Promotions = () => {
  const { user } = useAuth();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    destination_url: '',
    category: '',
    credit_budget: 10,
    image_url: ''
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await promotionsAPI.getAll();
      setPromotions(response.data);
    } catch (error) {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      destination_url: '',
      category: '',
      credit_budget: 10,
      image_url: ''
    });
    setEditingPromo(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (promo) => {
    setEditingPromo(promo);
    setFormData({
      title: promo.title,
      description: promo.description,
      destination_url: promo.destination_url,
      category: promo.category,
      credit_budget: promo.credit_budget,
      image_url: promo.image_url || ''
    });
    setDialogOpen(true);
  };

  const generateAIImage = async () => {
    if (!formData.title && !formData.description) {
      toast.error('Add a title or description first');
      return;
    }
    
    if (user?.plan !== 'power') {
      toast.error('AI Image generation requires Power plan');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const prompt = `Create a professional promotional banner for: ${formData.title}. ${formData.description}`;
      const response = await aiAPI.generateImage(prompt, 'professional');
      
      if (response.data.full_image) {
        // Convert base64 to data URL
        const dataUrl = `data:${response.data.mime_type};base64,${response.data.full_image}`;
        setFormData(prev => ({ ...prev, image_url: dataUrl }));
        toast.success('Image generated!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateAIContent = async () => {
    if (!formData.title && !formData.category) {
      toast.error('Add a title or select a category first');
      return;
    }
    
    if (user?.plan !== 'pro' && user?.plan !== 'power') {
      toast.error('AI content generation requires Pro or Power plan');
      return;
    }
    
    setGeneratingContent(true);
    try {
      const topic = formData.title || formData.category;
      const response = await aiAPI.generateCopy(topic, 'professional', formData.category);
      
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          title: response.data.headline || prev.title,
          description: response.data.description || prev.description
        }));
        toast.success('Content generated!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingPromo) {
        await promotionsAPI.update(editingPromo.id, formData);
        toast.success('Promotion updated!');
      } else {
        await promotionsAPI.create(formData);
        toast.success('Promotion created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      await promotionsAPI.delete(id);
      toast.success('Promotion deleted');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const toggleActive = async (promo) => {
    try {
      await promotionsAPI.update(promo.id, { is_active: !promo.is_active });
      toast.success(promo.is_active ? 'Promotion paused' : 'Promotion activated');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to update promotion');
    }
  };

  const getAIScore = async (promoId) => {
    try {
      const response = await aiAPI.getScore(promoId);
      const score = response.data.score;
      toast.success(`AI Score: ${score}/100`);
    } catch (error) {
      toast.error('Failed to get AI score');
    }
  };

  const planLimits = { free: 1, pro: 5, power: 100 };
  const maxPromos = planLimits[user?.plan || 'free'];
  const activePromos = promotions.filter(p => p.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promotions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white">My Promotions</h1>
          <p className="text-gray-400">
            {activePromos} / {maxPromos} active promotions ({user?.plan} plan)
          </p>
        </div>
        <Button 
          onClick={openCreateDialog}
          disabled={activePromos >= maxPromos}
          className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon btn-press"
          data-testid="create-promotion-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Promotion
        </Button>
      </div>

      {/* Promotions List */}
      {promotions.length === 0 ? (
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-white font-heading font-semibold text-xl mb-2">No promotions yet</h3>
            <p className="text-gray-400 mb-6">Create your first promotion to start getting traffic.</p>
            <Button 
              onClick={openCreateDialog}
              className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
            >
              Create Your First Promotion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {promotions.map((promo) => {
            const ctr = promo.views > 0 ? (promo.clicks / promo.views * 100).toFixed(1) : 0;
            const budgetUsed = promo.credit_budget > 0 ? (promo.credits_spent / promo.credit_budget * 100) : 0;
            
            return (
              <Card key={promo.id} className="bg-void-paper border-white/10 card-hover">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Promo Image */}
                    {promo.image_url && (
                      <div className="w-full lg:w-32 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-void">
                        <img 
                          src={promo.image_url} 
                          alt={promo.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Promo Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-semibold text-lg text-white truncate">
                          {promo.title}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          promo.is_active 
                            ? 'bg-neon-mint/10 text-neon-mint' 
                            : 'bg-gray-500/10 text-gray-400'
                        }`}>
                          {promo.is_active ? 'Active' : 'Paused'}
                        </span>
                        {promo.ai_score && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-neon-violet/10 text-neon-violet">
                            AI: {promo.ai_score}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">{promo.description}</p>
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <span className="px-2 py-0.5 bg-white/5 rounded">{promo.category}</span>
                        <a 
                          href={promo.destination_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-neon-mint transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {new URL(promo.destination_url).hostname}
                        </a>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
                          <Eye className="w-4 h-4" />
                          Views
                        </div>
                        <p className="font-mono font-bold text-white">{promo.views}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
                          <MousePointer className="w-4 h-4" />
                          Clicks
                        </div>
                        <p className="font-mono font-bold text-white">{promo.clicks}</p>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400 text-sm mb-1">CTR</div>
                        <p className="font-mono font-bold text-neon-mint">{ctr}%</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-gray-400 text-sm mb-1">
                          <Zap className="w-4 h-4" />
                          Budget
                        </div>
                        <p className="font-mono text-white">
                          <span className={budgetUsed >= 90 ? 'text-neon-amber' : ''}>
                            {promo.credits_spent}
                          </span>
                          <span className="text-gray-500">/{promo.credit_budget}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => getAIScore(promo.id)}
                        className="text-neon-violet hover:text-neon-violet hover:bg-neon-violet/10"
                        title="Get AI Score"
                      >
                        <Sparkles className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleActive(promo)}
                        className="text-gray-400 hover:text-white"
                        title={promo.is_active ? 'Pause' : 'Activate'}
                      >
                        {promo.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(promo)}
                        className="text-gray-400 hover:text-white"
                        title="Edit"
                        data-testid={`edit-promo-${promo.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(promo.id)}
                        className="text-gray-400 hover:text-red-400"
                        title="Delete"
                        data-testid={`delete-promo-${promo.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingPromo ? 'Edit Promotion' : 'Create Promotion'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Catchy headline for your promotion"
                required
                maxLength={60}
                className="bg-void border-white/10 focus:border-neon-mint"
                data-testid="promo-title-input"
              />
              <p className="text-gray-500 text-xs">{formData.title.length}/60 characters</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description">Description</Label>
                {(user?.plan === 'pro' || user?.plan === 'power') && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateAIContent}
                    disabled={generatingContent}
                    className="text-neon-violet hover:text-neon-violet/80 h-7 px-2"
                    data-testid="generate-ai-content-btn"
                  >
                    {generatingContent ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    AI Generate
                  </Button>
                )}
              </div>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What makes your offer compelling?"
                required
                maxLength={150}
                rows={3}
                className="bg-void border-white/10 focus:border-neon-mint resize-none"
                data-testid="promo-description-input"
              />
              <p className="text-gray-500 text-xs">{formData.description.length}/150 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="url">Destination URL</Label>
              <Input
                id="url"
                type="url"
                value={formData.destination_url}
                onChange={(e) => setFormData(prev => ({ ...prev, destination_url: e.target.value }))}
                placeholder="https://your-website.com"
                required
                className="bg-void border-white/10 focus:border-neon-mint"
                data-testid="promo-url-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  required
                >
                  <SelectTrigger className="bg-void border-white/10 focus:border-neon-mint" data-testid="promo-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-void-paper border-white/10">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-white hover:bg-white/5">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="budget">Credit Budget</Label>
                <Input
                  id="budget"
                  type="number"
                  min={1}
                  max={1000}
                  value={formData.credit_budget}
                  onChange={(e) => setFormData(prev => ({ ...prev, credit_budget: parseInt(e.target.value) || 0 }))}
                  className="bg-void border-white/10 focus:border-neon-mint"
                  data-testid="promo-budget-input"
                />
              </div>
            </div>
            
            {/* Image Section */}
            <div className="space-y-2">
              <Label>Promotion Image (Optional)</Label>
              {formData.image_url ? (
                <div className="relative rounded-lg overflow-hidden bg-void border border-white/10">
                  <img 
                    src={formData.image_url} 
                    alt="Promotion preview"
                    className="w-full h-40 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input
                    id="image_url"
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    className="bg-void border-white/10 focus:border-neon-mint"
                    data-testid="promo-image-url-input"
                  />
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-gray-500 text-xs">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <Button
                    type="button"
                    onClick={generateAIImage}
                    disabled={generatingImage || user?.plan !== 'power'}
                    variant="outline"
                    className="w-full border-neon-violet/50 text-neon-violet hover:bg-neon-violet/10"
                    data-testid="generate-ai-image-btn"
                  >
                    {generatingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {user?.plan === 'power' ? 'Generate with AI' : 'AI Image (Power Plan)'}
                  </Button>
                </div>
              )}
            </div>
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-gray-400"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
                data-testid="save-promo-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingPromo ? (
                  'Save Changes'
                ) : (
                  'Create Promotion'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Promotions;
